import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError, SITE_ORIGIN } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useUserWebSocket } from "@/lib/useUserWebSocket";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheckIcon, ImageIcon, VaultIcon, ArrowRightIcon, TrashIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import AdminUsers from "@/components/AdminUsers";
import AdminFounderReservations from "@/components/AdminFounderReservations";
import AdminEmailLog from "@/components/AdminEmailLog";
import AdminReleases from "@/components/AdminReleases";
import AdminUserReports from "@/components/AdminUserReports";
import AdminLoginSupport from "@/components/AdminLoginSupport";
import AdminStreamsManager from "@/components/AdminStreamsManager";
import IpGeoChip from "@/components/IpGeoChip";
import RiskBadge from "@/components/RiskBadge";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [verifStatus, setVerifStatus] = useState("pending"); // pending | approved | rejected
  const [verifQuery, setVerifQuery] = useState("");
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [apps, setApps] = useState([]);
  const [appStats, setAppStats] = useState({ seats_total: 250, seats_claimed: 0, spots_left: 250, pending: 0 });
  const [selectedApp, setSelectedApp] = useState(null);
  const [appReason, setAppReason] = useState("");
  const [whBuyers, setWhBuyers] = useState([]);
  const [selectedWh, setSelectedWh] = useState(null);
  const [whReason, setWhReason] = useState("");
  const [mods, setMods] = useState([]);
  const [modActions, setModActions] = useState([]);
  const [modEmail, setModEmail] = useState("");
  const [modNote, setModNote] = useState("");
  const [openFlags, setOpenFlags] = useState([]);
  const [wiping, setWiping] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState(null); // {title, body, endpoint, confirmWord, successMsg}
  const [confirmInput, setConfirmInput] = useState("");

  const openWipe = (cfg) => {
    setConfirmInput("");
    setWipeConfirm(cfg);
  };

  const runWipe = async () => {
    if (!wipeConfirm) return;
    if (confirmInput.trim().toUpperCase() !== wipeConfirm.confirmWord) {
      toast.error(`Type ${wipeConfirm.confirmWord} to confirm.`);
      return;
    }
    setWiping(true);
    try {
      const { data } = await api.post(wipeConfirm.endpoint);
      toast.success(data.message || wipeConfirm.successMsg || "Done");
      setWipeConfirm(null);
      setConfirmInput("");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWiping(false);
    }
  };

  const wipeDemoData = () =>
    openWipe({
      title: "Wipe demo accounts?",
      body: "Permanently deletes every user whose email begins with demo_ and all of their streams, listings, bids, and orders. Your real users are safe.",
      endpoint: "/admin/wipe-demo",
      confirmWord: "WIPE",
      successMsg: "Demo data wiped",
    });

  const wipeAllContent = () =>
    openWipe({
      title: "Wipe ALL content?",
      body: "Deletes every stream, listing, bid, order, tip, chat, follow, and notification across the entire platform. User accounts are kept intact.",
      endpoint: "/admin/wipe-all-content",
      confirmWord: "WIPE",
      successMsg: "All content wiped",
    });

  const factoryReset = () =>
    openWipe({
      title: "Factory reset — DELETE all users except you?",
      body: "Deletes every non-admin user AND every stream, listing, bid, order, tip, chat. Only your admin account survives. This cannot be undone.",
      endpoint: "/admin/factory-reset",
      confirmWord: "RESET",
      successMsg: "Factory reset complete",
    });

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      navigate("/");
      return;
    }
    refresh();
    // eslint-disable-next-line
  }, [user, loading, verifStatus]);

  const refresh = async () => {
    try {
      const verifParams = { status: verifStatus };
      if (verifQuery.trim()) verifParams.q = verifQuery.trim();
      const [p, s, a, wh, m, ma, fl] = await Promise.all([
        api.get("/admin/verifications", { params: verifParams }),
        api.get("/admin/stats"),
        api.get("/admin/seller-applications", { params: { status: "pending" } }),
        api.get("/admin/wholesale-credentials", { params: { status: "pending" } }),
        api.get("/admin/moderators"),
        api.get("/admin/mod-actions", { params: { limit: 20 } }),
        api.get("/mod/flags", { params: { status: "open" } }),
      ]);
      setPending(p.data.items);
      setStats(s.data);
      setApps(a.data.items);
      setAppStats(a.data.stats);
      setWhBuyers(wh.data.items);
      setMods(m.data.items || []);
      setModActions(ma.data.items || []);
      setOpenFlags(fl.data.items || []);
    } catch (e) {
      console.error(e);
    }
  };

  // ── Real-time push: refresh the queues the instant something moves
  // (verification submitted / decided, wholesale credentials submitted,
  //  etc.) so admins never have to hit reload. Only fires while the
  // admin is on this page — hook unsubscribes on unmount.
  useUserWebSocket({
    enabled: !!user && user.role === "admin",
    onEvent: (msg) => {
      if (
        msg.type === "verification.submitted" ||
        msg.type === "verification.decision" ||
        msg.type === "wholesale.submitted" ||
        msg.type === "wholesale.decision" ||
        msg.type === "seller_application.submitted" ||
        msg.type === "seller_application.decision"
      ) {
        refresh();
        if (msg.type === "verification.submitted") {
          toast.info(`New ID verification from ${msg.name || msg.email || "a user"}`);
        }
      }
    },
  });

  const promoteModerator = async (e) => {
    e?.preventDefault();
    if (!modEmail.trim()) return;
    try {
      await api.post("/admin/moderators", {
        email: modEmail.trim(),
        note: modNote.trim() || null,
      });
      toast.success(`${modEmail} is now a moderator`);
      setModEmail("");
      setModNote("");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const demoteModerator = async (mod) => {
    if (!window.confirm(`Remove moderator access from ${mod.name || mod.email}?`)) return;
    try {
      await api.delete(`/admin/moderators/${mod.user_id}`);
      toast.success("Moderator removed");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resolveFlag = async (flag, action) => {
    const note = action === "dismissed"
      ? window.prompt("Dismissal note (optional)", "") || null
      : window.prompt("Resolution note (optional)", "") || null;
    try {
      await api.post(`/admin/flags/${flag.id}/resolve`, { action, note });
      toast.success(`Flag ${action}`);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const decide = async (approved) => {
    try {
      await api.post(`/admin/verifications/${selected.user_id}`, {
        approved,
        reason: approved ? null : reason,
      });
      toast.success(`User ${approved ? "approved" : "rejected"}`);
      setSelected(null);
      setReason("");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const decideApplication = async (approved) => {
    try {
      await api.post(`/admin/seller-applications/${selectedApp.id}`, {
        approved,
        reason: approved ? null : appReason,
      });
      toast.success(`Application ${approved ? "approved" : "rejected"}`);
      setSelectedApp(null);
      setAppReason("");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const decideWholesale = async (approved) => {
    try {
      await api.post(`/admin/wholesale-credentials/${selectedWh.user_id}`, {
        approved,
        reason: approved ? null : whReason,
      });
      toast.success(`Wholesale buyer ${approved ? "approved" : "rejected"}`);
      setSelectedWh(null);
      setWhReason("");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="admin-page">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheckIcon size={24} className="text-[#1D78AB]" />
              <p className="overline text-zinc-500">ADMIN / TRUST & SAFETY</p>
            </div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
              Verification queue
            </h1>
          </div>
          <Link
            to="/admin/revenue"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-500 to-emerald-700 px-5 py-3.5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-revenue-link"
          >
            <VaultIcon size={22} weight="fill" />
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase text-white/80">
                OWNER COCKPIT
              </p>
              <p className="font-display font-black text-base">Platform Revenue</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/admin/sponsorships"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-400 to-amber-600 px-5 py-3.5 text-amber-950 hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-sponsorships-link"
          >
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase opacity-80">
                PAID PLACEMENTS
              </p>
              <p className="font-display font-black text-base">Sponsorships</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/admin/payments/health"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-[#1D78AB] bg-gradient-to-br from-sky-500 to-[#1D78AB] px-5 py-3.5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-payments-health-link"
          >
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase text-white/80">
                STRIPE · PAYPAL
              </p>
              <p className="font-display font-black text-base">Payments health</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/admin/cinema/health"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-amber-500/60 bg-gradient-to-br from-zinc-900 to-zinc-700 px-5 py-3.5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-cinema-health-link"
          >
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase text-amber-300/90">
                MUX · 4K
              </p>
              <p className="font-display font-black text-base">Cinema health</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/admin/support"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-rose-400 bg-gradient-to-br from-rose-500 to-rose-700 px-5 py-3.5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-support-link"
          >
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase text-white/80">
                CUSTOMERS
              </p>
              <p className="font-display font-black text-base">Support inbox</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/admin/orders"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-zinc-700 bg-gradient-to-br from-zinc-800 to-zinc-950 px-5 py-3.5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-orders-link"
          >
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase text-white/80">
                OPERATIONS
              </p>
              <p className="font-display font-black text-base">Orders cockpit</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/admin/scale-health"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-600 to-emerald-800 px-5 py-3.5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-scale-health-link"
          >
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase text-emerald-100">
                INFRA
              </p>
              <p className="font-display font-black text-base">Scale health</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/admin/security"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-rose-400 bg-gradient-to-br from-rose-600 to-rose-800 px-5 py-3.5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-security-link"
          >
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase text-rose-100">
                TRUST & SAFETY
              </p>
              <p className="font-display font-black text-base">Security cockpit</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/admin/beacon"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-500 to-amber-700 px-5 py-3.5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-beacon-link"
          >
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase text-amber-100">
                HOMEPAGE
              </p>
              <p className="font-display font-black text-base">🌀 Beacon Spotlight</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/admin/boost"
            className="group inline-flex items-center gap-3 rounded-2xl border-2 border-fuchsia-300 bg-gradient-to-br from-fuchsia-500 to-purple-700 px-5 py-3.5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="admin-boost-link"
          >
            <div className="text-left leading-tight">
              <p className="text-[10px] font-mono tracking-widest uppercase text-fuchsia-100">
                Growth Ops
              </p>
              <p className="font-display font-black text-base">⚡ Live Boost</p>
            </div>
            <ArrowRightIcon size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-3 md:grid-cols-6 gap-2 mt-8">
          <Stat label="Users" value={stats.users} />
          <Stat label="Pending" value={stats.pending_verifications} highlight />
          <Stat label="Approved" value={stats.approved_users} />
          <Stat label="Listings" value={stats.listings} />
          <Stat label="Live streams" value={stats.active_streams} />
          <Stat label="Paid orders" value={stats.payments} />
        </div>

        <div className="mt-6 p-4 border-2 border-red-200 bg-red-50 rounded-2xl flex items-center justify-between flex-wrap gap-3" data-testid="admin-wipe-demo-banner">
          <div className="flex items-center gap-3">
            <TrashIcon size={20} className="text-red-600" />
            <div>
              <div className="font-bold text-red-900">Wipe demo data</div>
              <div className="text-xs text-red-700/80">
                Removes every account with a <code className="font-mono">demo_</code> email prefix and all of their streams, listings, bids, orders, and tips.
              </div>
            </div>
          </div>
          <Button
            onClick={wipeDemoData}
            disabled={wiping}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="admin-wipe-demo-btn"
          >
            {wiping ? "Wiping…" : "Wipe demo data"}
          </Button>
          <Button
            onClick={wipeAllContent}
            disabled={wiping}
            variant="outline"
            className="border-red-600 text-red-700 hover:bg-red-100"
            data-testid="admin-wipe-all-btn"
          >
            {wiping ? "Wiping…" : "Wipe ALL content"}
          </Button>
          <Button
            onClick={factoryReset}
            disabled={wiping}
            variant="outline"
            className="border-red-800 text-red-900 hover:bg-red-200 font-bold"
            data-testid="admin-factory-reset-btn"
          >
            {wiping ? "Wiping…" : "Factory reset (nuke everything)"}
          </Button>
        </div>

        <div className="mt-10 border border-zinc-200 bg-white">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
            <div className="font-display font-bold">
              ID verifications
              <span className="ml-2 text-xs font-normal text-zinc-500">
                ({pending.length} {verifStatus})
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={verifQuery}
                onChange={(e) => setVerifQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && refresh()}
                placeholder="Search name, email, or IP…"
                className="w-56 h-8 text-sm"
                data-testid="verif-search-input"
              />
              <div className="inline-flex rounded-full border border-zinc-200 p-0.5 bg-zinc-50" data-testid="verif-status-tabs">
                {["pending", "approved", "rejected"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setVerifStatus(s)}
                    className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full transition ${
                      verifStatus === s
                        ? "bg-[#1D78AB] text-white shadow"
                        : "text-zinc-600 hover:text-zinc-900"
                    }`}
                    data-testid={`verif-tab-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Button variant="outline" onClick={refresh} data-testid="admin-refresh-btn">Refresh</Button>
            </div>
          </div>
          {pending.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              <p>
                {verifStatus === "pending"
                  ? "No pending verifications. The queue is clear."
                  : `No ${verifStatus} verifications.`}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {pending.map((u) => (
                <div key={u.user_id} className="p-4 flex items-center gap-4" data-testid={`pending-${u.user_id}`}>
                  <div className="h-10 w-10 bg-zinc-100 grid place-items-center font-semibold">
                    {u.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-xs text-zinc-500">{u.email}</div>
                    {(u.signup_ip || u.verification_ip) && (
                      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`ip-${u.user_id}`}>
                        <IpGeoChip
                          label="signup:"
                          ip={u.signup_ip}
                          geo={u.signup_ip_geo}
                          testid={`signup-ip-${u.user_id}`}
                        />
                        {u.verification_ip && u.verification_ip !== u.signup_ip && (
                          <IpGeoChip
                            label="id:"
                            ip={u.verification_ip}
                            geo={u.verification_ip_geo}
                            testid={`verif-ip-${u.user_id}`}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase ${
                    u.segment === "wholesale" ? "bg-amber-500 text-white" : "bg-[#1D78AB] text-white"
                  }`}>
                    {u.segment}
                  </span>
                  <RiskBadge risk={u.risk} testid={`risk-${u.user_id}`} />
                  <span className="text-xs text-zinc-500 hidden md:inline">{u.account_type.replace("_", " ")}</span>
                  <Button
                    variant="outline"
                    onClick={() => setSelected(u)}
                    data-testid={`review-${u.user_id}`}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <AdminEmailLog />

        <AdminLoginSupport />

        <AdminUserReports />

        <AdminReleases />

        {/* --- Seller Applications (Founder Seller waitlist) --- */}
        <div className="mt-10 border border-zinc-200 bg-white rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-display font-bold">Founder Seller applications</div>
              <div className="text-xs text-zinc-500 mt-0.5 font-mono">
                {appStats.seats_claimed}/{appStats.seats_total} seats claimed ·{" "}
                <span className="text-[#1D78AB] font-bold">{appStats.spots_left} spots left</span>
                {" "}· {appStats.pending} pending
              </div>
            </div>
            <Button variant="outline" onClick={refresh} data-testid="admin-apps-refresh-btn">
              Refresh
            </Button>
          </div>
          {apps.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              <p>No pending Founder Seller applications.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {apps.map((a) => (
                <div
                  key={a.id}
                  className="p-4 flex items-center gap-4 flex-wrap"
                  data-testid={`application-${a.id}`}
                >
                  <div className="h-10 w-10 bg-zinc-100 rounded-xl grid place-items-center font-semibold">
                    {a.brand_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold">{a.brand_name}</div>
                    <div className="text-xs text-zinc-500">
                      {a.contact_name} · {a.email}
                    </div>
                    {a.applicant_ip && (
                      <div className="mt-0.5" data-testid={`app-ip-${a.id}`}>
                        <IpGeoChip
                          label="ip:"
                          ip={a.applicant_ip}
                          geo={a.applicant_ip_geo}
                          testid={`founder-ip-${a.id}`}
                        />
                      </div>
                    )}
                  </div>
                  <span
                    className={`pill ${
                      a.segment === "wholesale"
                        ? "bg-amber-500 text-white"
                        : "bg-[#1D78AB] text-white"
                    }`}
                  >
                    {a.segment}
                  </span>
                  <RiskBadge risk={a.risk} testid={`risk-app-${a.id}`} />
                  <span className="text-xs text-zinc-500 hidden md:inline">
                    {a.est_monthly_gmv_usd
                      ? `$${Number(a.est_monthly_gmv_usd).toLocaleString()}/mo`
                      : "—"}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedApp(a)}
                    data-testid={`review-application-${a.id}`}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Dialog open={!!selectedApp} onOpenChange={(v) => !v && setSelectedApp(null)}>
          <DialogContent className="max-w-2xl">
            {selectedApp && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">
                    {selectedApp.brand_name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="overline text-zinc-500 block">Contact</span>
                      {selectedApp.contact_name}
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">Email</span>
                      {selectedApp.email}
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">Phone</span>
                      {selectedApp.phone || "—"}
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">Social</span>
                      {selectedApp.social_handle || "—"}
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">Segment</span>
                      {selectedApp.segment}
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">Monthly GMV</span>
                      {selectedApp.est_monthly_gmv_usd
                        ? `$${Number(selectedApp.est_monthly_gmv_usd).toLocaleString()}`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <span className="overline text-zinc-500 block mb-1">Categories</span>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedApp.categories?.map((c) => (
                        <span key={c} className="pill bg-zinc-900 text-white">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="overline text-zinc-500 block mb-1">Pitch</span>
                    <p className="text-sm bg-zinc-50 border border-zinc-200 rounded-xl p-3 whitespace-pre-wrap">
                      {selectedApp.pitch}
                    </p>
                  </div>
                  <Input
                    placeholder="Rejection reason (optional)"
                    value={appReason}
                    onChange={(e) => setAppReason(e.target.value)}
                    data-testid="app-rejection-reason-input"
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => decideApplication(false)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="admin-reject-app-btn"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => decideApplication(true)}
                    className="bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full"
                    data-testid="admin-approve-app-btn"
                  >
                    Approve Founder seat
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* --- Wholesale buyer credentials review --- */}
        <div className="mt-10 border border-zinc-200 bg-white rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-display font-bold">Wholesale buyer credentials</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {whBuyers.length} pending — reseller permit + TIN + business entity review
              </div>
            </div>
            <Button variant="outline" onClick={refresh} data-testid="admin-wh-refresh-btn">
              Refresh
            </Button>
          </div>
          {whBuyers.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              <p>No pending wholesale buyer applications.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {whBuyers.map((b) => (
                <div
                  key={b.user_id}
                  className="p-4 flex items-center gap-4 flex-wrap"
                  data-testid={`wholesale-buyer-${b.user_id}`}
                >
                  <div className="h-10 w-10 bg-amber-100 rounded-xl grid place-items-center font-semibold">
                    {b.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold">
                      {b.wholesale_credentials?.business_name || b.name}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {b.email} · {b.wholesale_credentials?.business_entity_type || "—"}
                    </div>
                    {(b.wholesale_credentials?.submission_ip || b.signup_ip) && (
                      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`wh-ip-${b.user_id}`}>
                        {b.wholesale_credentials?.submission_ip && (
                          <IpGeoChip
                            label="submit:"
                            ip={b.wholesale_credentials.submission_ip}
                            geo={b.wholesale_credentials.submission_ip_geo}
                            testid={`wh-submit-ip-${b.user_id}`}
                          />
                        )}
                        {b.signup_ip && b.signup_ip !== b.wholesale_credentials?.submission_ip && (
                          <IpGeoChip
                            label="signup:"
                            ip={b.signup_ip}
                            geo={b.signup_ip_geo}
                            testid={`wh-signup-ip-${b.user_id}`}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-zinc-500 hidden md:inline">
                    Permit:{" "}
                    <span className="font-mono">
                      {b.wholesale_credentials?.resale_permit_number || "—"}
                    </span>
                  </span>
                  <RiskBadge risk={b.risk} testid={`risk-wh-${b.user_id}`} />
                  <Button
                    variant="outline"
                    onClick={() => setSelectedWh(b)}
                    data-testid={`review-wholesale-${b.user_id}`}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <AdminUsers currentAdminId={user.user_id} />

        <AdminFounderReservations />

        {/* --- Moderators --- */}
        <div className="mt-10 border border-zinc-200 bg-white rounded-2xl overflow-hidden" data-testid="admin-moderators-section">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-display font-bold">Moderators</div>
              <div className="text-xs text-zinc-500 mt-0.5 font-mono">
                {mods.length} active · monitor live shows + chat
              </div>
            </div>
            <Button variant="outline" onClick={refresh} data-testid="admin-mods-refresh-btn">
              Refresh
            </Button>
          </div>

          <form onSubmit={promoteModerator} className="p-4 grid sm:grid-cols-[1fr,1fr,auto] gap-2 border-b border-zinc-200 bg-zinc-50/60">
            <Input
              type="email"
              placeholder="user@email.com (must already have a Bidorah account)"
              value={modEmail}
              onChange={(e) => setModEmail(e.target.value)}
              data-testid="admin-promote-mod-email"
            />
            <Input
              type="text"
              placeholder="Internal note (optional)"
              value={modNote}
              onChange={(e) => setModNote(e.target.value)}
              maxLength={280}
              data-testid="admin-promote-mod-note"
            />
            <Button type="submit" disabled={!modEmail.trim()} data-testid="admin-promote-mod-submit">
              Promote to moderator
            </Button>
          </form>

          <div className="divide-y divide-zinc-200">
            {mods.length === 0 && (
              <div className="p-6 text-sm text-zinc-500">No moderators yet. Promote a trusted user above.</div>
            )}
            {mods.map((m) => (
              <div key={m.user_id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-mod-row-${m.user_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{m.name}</div>
                  <div className="text-xs text-zinc-500 font-mono truncate">{m.email}</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    Mod since {m.mod_since ? new Date(m.mod_since).toLocaleDateString() : "—"}
                    {m.last_action_at && (
                      <> · Last action {new Date(m.last_action_at).toLocaleString()} ({m.last_action})</>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => demoteModerator(m)}
                  data-testid={`admin-demote-mod-${m.user_id}`}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* --- Admin: live show controls --- */}
        <AdminStreamsManager />

        {/* --- Open stream flags --- */}
        <div className="mt-10 border border-zinc-200 bg-white rounded-2xl overflow-hidden" data-testid="admin-flags-section">
          <div className="p-4 border-b border-zinc-200">
            <div className="font-display font-bold">Stream flags — open ({openFlags.length})</div>
            <div className="text-xs text-zinc-500 mt-0.5 font-mono">Flags raised by moderators awaiting admin review</div>
          </div>
          <div className="divide-y divide-zinc-200">
            {openFlags.length === 0 && (
              <div className="p-6 text-sm text-zinc-500">No open flags. 👏</div>
            )}
            {openFlags.map((f) => (
              <div key={f.id} className="p-4 space-y-2" data-testid={`admin-flag-row-${f.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-2">
                      <span className="pill pill-dark text-[10px] uppercase tracking-wider">{f.category}</span>
                      {f.stream_title || f.stream_id}
                    </div>
                    <div className="text-xs text-zinc-500">
                      Flagged by <span className="font-semibold">{f.flagged_by_name}</span> · {new Date(f.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/stream/${f.stream_id}`} className="text-xs text-[#1D78AB] underline">Open stream</Link>
                    <Button size="sm" variant="outline" onClick={() => resolveFlag(f, "dismissed")} data-testid={`admin-flag-dismiss-${f.id}`}>
                      Dismiss
                    </Button>
                    <Button size="sm" onClick={() => resolveFlag(f, "resolved")} data-testid={`admin-flag-resolve-${f.id}`}>
                      Mark resolved
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-zinc-700 leading-relaxed bg-zinc-50 rounded-lg p-3 border border-zinc-200">
                  {f.reason}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* --- Moderator activity log --- */}
        <div className="mt-10 mb-16 border border-zinc-200 bg-white rounded-2xl overflow-hidden" data-testid="admin-mod-audit-section">
          <div className="p-4 border-b border-zinc-200">
            <div className="font-display font-bold">Moderator audit log</div>
            <div className="text-xs text-zinc-500 mt-0.5 font-mono">Last 20 actions</div>
          </div>
          <div className="divide-y divide-zinc-200">
            {modActions.length === 0 && (
              <div className="p-6 text-sm text-zinc-500">No activity yet.</div>
            )}
            {modActions.map((a) => (
              <div key={a.id} className="p-3 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm min-w-0">
                  <span className="font-semibold">{a.mod_name}</span>
                  <span className="text-zinc-500"> · </span>
                  <span className="font-mono text-xs text-[#1D78AB]">{a.action}</span>
                  <span className="text-zinc-500"> · </span>
                  <span className="text-xs text-zinc-600 truncate">{a.target_type} {a.target_id?.slice(-8)}</span>
                  {a.reason && <span className="text-zinc-500 text-xs"> — {a.reason}</span>}
                </div>
                <div className="text-[11px] text-zinc-400 font-mono">
                  {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Dialog open={!!wipeConfirm} onOpenChange={(v) => !v && setWipeConfirm(null)}>
          <DialogContent data-testid="admin-wipe-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-700">{wipeConfirm?.title}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-700 leading-relaxed">{wipeConfirm?.body}</p>
            <div className="mt-2">
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                Type <span className="text-red-600 font-bold">{wipeConfirm?.confirmWord}</span> to proceed
              </label>
              <Input
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                autoFocus
                placeholder={wipeConfirm?.confirmWord}
                className="mt-1"
                data-testid="admin-wipe-confirm-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setWipeConfirm(null)} disabled={wiping}>
                Cancel
              </Button>
              <Button
                onClick={runWipe}
                disabled={
                  wiping ||
                  confirmInput.trim().toUpperCase() !== wipeConfirm?.confirmWord
                }
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="admin-wipe-confirm-submit"
              >
                {wiping ? "Wiping…" : "Yes, wipe it"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!selectedWh} onOpenChange={(v) => !v && setSelectedWh(null)}>
          <DialogContent className="max-w-2xl">
            {selectedWh && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">
                    {selectedWh.wholesale_credentials?.business_name || selectedWh.name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="overline text-zinc-500 block">Contact</span>
                      {selectedWh.name} · {selectedWh.email}
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">Entity type</span>
                      {selectedWh.wholesale_credentials?.business_entity_type || "—"}
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">Business state</span>
                      {selectedWh.wholesale_credentials?.business_state || "—"}
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">TIN (last 4)</span>
                      <span className="font-mono">
                        ••••• {selectedWh.wholesale_credentials?.tin_last4 || "----"}
                      </span>
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">Permit #</span>
                      <span className="font-mono">
                        {selectedWh.wholesale_credentials?.resale_permit_number || "—"}
                      </span>
                    </div>
                    <div>
                      <span className="overline text-zinc-500 block">Permit state</span>
                      {selectedWh.wholesale_credentials?.resale_permit_state || "—"}
                    </div>
                  </div>
                  <Input
                    placeholder="Rejection reason (optional)"
                    value={whReason}
                    onChange={(e) => setWhReason(e.target.value)}
                    data-testid="wholesale-rejection-reason-input"
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => decideWholesale(false)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="admin-reject-wholesale-btn"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => decideWholesale(true)}
                    className="bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full"
                    data-testid="admin-approve-wholesale-btn"
                  >
                    Approve wholesale access
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
          <DialogContent className="max-w-2xl">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">
                    Review: {selected.name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="overline text-zinc-500 block">Email</span>{selected.email}</div>
                    <div><span className="overline text-zinc-500 block">Segment</span>{selected.segment}</div>
                    <div><span className="overline text-zinc-500 block">Account type</span>{selected.account_type}</div>
                    <div><span className="overline text-zinc-500 block">Submitted</span>{selected.created_at ? new Date(selected.created_at).toLocaleDateString() : "—"}</div>
                  </div>
                  <div>
                    <p className="overline text-zinc-500 mb-2">SUBMITTED DOCUMENT</p>
                    {selected.id_document_path ? (
                      <div className="border border-zinc-200 bg-zinc-100 p-2">
                        <img
                          src={`${SITE_ORIGIN}/api/files/${selected.id_document_path}`}
                          alt="ID"
                          className="max-h-96 mx-auto"
                          data-testid="admin-id-document"
                          onError={(e) => { e.target.style.display = "none"; e.target.parentNode.innerHTML = '<div class="p-12 text-center text-zinc-500"><p class="text-sm">Document is a PDF — <a href="'+e.target.src+'" target="_blank" class="text-[#1D78AB] underline">open in new tab</a></p></div>'; }}
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-zinc-500">No document uploaded.</p>
                    )}
                  </div>
                  <Input
                    placeholder="Rejection reason (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="rejection-reason-input"
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => decide(false)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="admin-reject-btn"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => decide(true)}
                    className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
                    data-testid="admin-approve-btn"
                  >
                    Approve
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`border p-4 ${highlight ? "border-[#1D78AB] bg-[#1D78AB]/5" : "border-zinc-200 bg-white"}`}>
      <div className="overline text-zinc-500">{label}</div>
      <div className="font-display font-black text-2xl mt-1">{value ?? "—"}</div>
    </div>
  );
}
